import { ApiError } from '@/lib/api';
import { type ChatRoomStatus, assertNever } from '@matter_extractor/shared';

/**
 * Which screen a chat room is on (chat spec §3, §4, §5).
 *
 * Pure and tested, the régime `overviewRefetchInterval` and `parcoursStopAt`
 * already hold in this package: what decides which of ten screens somebody
 * lands on has to be replayable without a router and without a server.
 */

/** The three phases a resolved room can be in. */
export type ChatPhase = 'lobby' | 'conversation' | 'ending';

/**
 * The room's status mapped onto its screen.
 *
 * `closed` and `abandoned` are both an END: a guest who left is a finding about
 * the conduct (§11) and not an error, so it gets the same end screen rather
 * than a fourth one.
 */
export function chatRoomPhase(status: ChatRoomStatus): ChatPhase {
  switch (status) {
    case 'open':
      return 'lobby';
    case 'chatting':
      return 'conversation';
    case 'closed':
    case 'abandoned':
      return 'ending';
    default:
      return assertNever(status, 'chat room phase');
  }
}

/**
 * Whether a failure means THE LINK IS DEAD - and nothing else does.
 *
 * **Only a 410.** §5 requires an expired link and an unknown one to be
 * indistinguishable, and the backend answers 410 for both, so one status covers
 * the whole of what S8 is for. It does NOT cover anything else, and that
 * distinction is the point of this function existing:
 *
 * - a 429 from the per-address ceiling is « réessayez », not « ce lien est
 *   mort ». Read as a dead link it would tell somebody holding a perfectly good
 *   invitation to give up.
 * - a dropped connection is a BANNER and never a step backwards (§4.5). The
 *   dead-link screen is a one-way door in the UI: it offers nothing to retry,
 *   so a one-second outage would end an interview that is still running.
 * - a schema `parse` that fails is our bug, and painting it as an expired link
 *   is how a deploy that broke the contract gets diagnosed as « les liens
 *   n'ont pas marché ce jour-là ».
 *
 * Deliberately narrower than `isSessionGone`, which also folds 404 in: the chat
 * door never answers 404 - it answers 410 for an unknown token on purpose - so
 * a 404 here would mean the ROUTE is wrong, which is not a dead link either.
 */
export function isChatLinkGone(error: unknown): boolean {
  return error instanceof ApiError && error.status === 410;
}
